/**
 * space_comfort.ts — the SECTOR's comfort and readability rules (space campaign L, 2026-10-01; gap 27, report §10.1
 * last ¶, §22.2 last ¶). Pure apart from two tiny stores (the desktop choices, the shared motion meter) — no three.
 *
 * WHAT IT DECIDES
 *  · `sectorEffects` — ONE decision for every SECONDARY effect (the trauma shake, the radial streaks, the FOV kick, the
 *    velocity dust, the warp tunnel's spin, your own warp flash). It has no aim, damage or threat field on purpose: the
 *    report's rule is "reduced motion removes secondary shake, speed streaks and intrusive transitions while preserving
 *    aim, damage and threat data". Desktop: System (follows `prefers-reduced-motion`) / Full / Reduced / Off — the
 *    Halo-Infinite-style 0–100 % scale XAG 117 cites. Headset: the VR comfort choice; the camera never shakes in a headset
 *    (Meta: never move the view without the user's input; three overwrites the camera with the head pose anyway).
 *  · `headsetBank` — what a headset banks. THE AERO LAW banks the PICTURE (the rig) into a turn; in a headset that is an
 *    uncommanded WORLD roll on every yaw, and roll is the most sickening vection axis (Roll SSQ highest of the three
 *    axes; yaw the mildest). Default off; reduced (35 %, ≤ 15°) and full (desktop parity) are choices.
 *  · Roll comfort — a roll-rate cap, an eased AUTO-LEVEL after an idle moment (Liftoff's horizon mode, Warframe's
 *    Railjack auto-level, Ace Combat's "Optimum"), or a horizon LOCK (Elite's "maintain horizon" for its SRV).
 *  · The vignette's CUE — the ship's linear ACCELERATION and its body-axis ANGULAR rates (roll weighted), never the
 *    speed and never the head: the vestibular system "respond[s] to changes in motion but not to constant velocity"
 *    (Meta); Fernandes & Feiner restrict the field of view on the vehicle's linear and angular motion. Mapped INTO the
 *    existing `vignetteTarget` so the ground's tuning, the vignette mesh and the Comfort page stay the one model.
 *
 * THE LEVER: `localStorage atlas:space-comfort-v1 = 'off'` restores the pre-slice presentation everywhere this slice
 * touched (the desktop pad mapping in the headset, no VR cockpit, every effect at full, the sky's old feed).
 */
import type { SpaceBank, SpaceCue, SpaceEffects, SpaceRoll, XRComfort } from './xr_comfort.js';
import {
  VIGNETTE_SPEED_DEAD_MPS, VIGNETTE_SPEED_FULL_RIDE_MPS, VIGNETTE_YAW_DEAD_DPS, VIGNETTE_YAW_FULL_RIDE_DPS, type RigMotion,
} from './xr_locomotion.js';

type Store = Pick<Storage, 'getItem' | 'setItem'>;
function safeStorage(): Store | null { try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; } }

// ── The lever ─────────────────────────────────────────────────────────────────────────────────────────────────────
export const SPACE_COMFORT_LEVER_KEY = 'atlas:space-comfort-v1';
/** The slice's rollback lever (ABSENT = ON, like the server's flags). */
export function spaceComfortOn(store: Store | null = safeStorage()): boolean {
  try { return store?.getItem(SPACE_COMFORT_LEVER_KEY) !== 'off'; } catch { return true; }
}

// ── The desktop choices (never in `atlas:vr-comfort`, never in `atlas:graphics`) ───────────────────────────────────
export type SectorMotion = 'system' | 'full' | 'reduced' | 'off';
export interface DesktopSpaceComfort { motion: SectorMotion; hudScale: number }
export const DESKTOP_SPACE_COMFORT_KEY = 'atlas:space-comfort';
export const HUD_SCALE_MIN = 0.75, HUD_SCALE_MAX = 1.5;
/** The menu's steps (Options → Controls → Flight; the VR Comfort page cycles the same list). */
export const HUD_SCALE_CHOICES = [0.85, 1, 1.15, 1.3, 1.5] as const;
export const DESKTOP_DEFAULTS: Readonly<DesktopSpaceComfort> = Object.freeze({ motion: 'system', hudScale: 1 });
/** The scale a motion step applies to every secondary effect. */
export const MOTION_SCALE: Readonly<Record<'full' | 'reduced' | 'off', number>> = { full: 1, reduced: 0.35, off: 0 };

export function hudScaleClamp(v: unknown, fallback = 1): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(HUD_SCALE_MAX, Math.max(HUD_SCALE_MIN, v)) : fallback;
}

export function sanitizeDesktop(raw: unknown): DesktopSpaceComfort {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof DesktopSpaceComfort, unknown>>;
  const m = r.motion;
  return {
    motion: m === 'full' || m === 'reduced' || m === 'off' ? m : 'system',
    hudScale: hudScaleClamp(r.hudScale, 1),
  };
}

let desktop: DesktopSpaceComfort | null = null;
export function desktopSpaceComfort(store: Store | null = safeStorage()): DesktopSpaceComfort {
  if (desktop) return desktop;
  try { desktop = sanitizeDesktop(JSON.parse(store?.getItem(DESKTOP_SPACE_COMFORT_KEY) ?? 'null')); } catch { desktop = sanitizeDesktop(null); }
  return desktop;
}
export function setDesktopSpaceComfort(patch: Partial<DesktopSpaceComfort>, store: Store | null = safeStorage()): DesktopSpaceComfort {
  desktop = sanitizeDesktop({ ...desktopSpaceComfort(store), ...patch });
  try { store?.setItem(DESKTOP_SPACE_COMFORT_KEY, JSON.stringify(desktop)); } catch { /* private window — the session keeps it */ }
  return desktop;
}
/** Harnesses: forget the cached desktop choices (re-read the store). */
export function resetDesktopCache(): void { desktop = null; }

/** The OS's reduce-motion preference (cached, follows the media query's change event). */
let reducedMq: { matches: boolean } | null | undefined;
export function prefersReducedMotion(): boolean {
  if (reducedMq === undefined) {
    try {
      const mq = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null;
      reducedMq = mq;
    } catch { reducedMq = null; }
  }
  return !!reducedMq?.matches;
}

// ── THE EFFECTS DECISION ──────────────────────────────────────────────────────────────────────────────────────────
/** Every SECONDARY effect the sector draws, 0 (gone) … 1 (as authored). Never an aim, damage or threat channel. */
export interface SectorEffects { shake: number; streaks: number; fovKick: number; dust: number; tunnelSpin: number; warpFlash: number }
export const ALL_EFFECTS: Readonly<SectorEffects> = Object.freeze({ shake: 1, streaks: 1, fovKick: 1, dust: 1, tunnelSpin: 1, warpFlash: 1 });

export interface EffectsInput {
  /** The slice lever (`spaceComfortOn`). Off → every effect as authored (the pre-slice behaviour). */
  on: boolean;
  /** A headset is presenting. */
  presenting: boolean;
  /** The VR comfort choice (read only while presenting). */
  vrEffects: SpaceEffects;
  /** The desktop choice (read only on the desktop). */
  desktopMotion: SectorMotion;
  /** The OS asks for reduced motion (`prefers-reduced-motion: reduce`). */
  prefersReduced: boolean;
}

export function sectorEffects(i: EffectsInput): SectorEffects {
  if (!i.on) return { ...ALL_EFFECTS };
  if (i.presenting) {
    // A headset never shakes the view or kicks the FOV (the head owns the camera — three replaces both each frame).
    return i.vrEffects === 'full'
      ? { shake: 0, streaks: 1, fovKick: 0, dust: 1, tunnelSpin: 1, warpFlash: 1 }
      : { shake: 0, streaks: 0, fovKick: 0, dust: 0.35, tunnelSpin: 0, warpFlash: 0.25 };
  }
  const m: 'full' | 'reduced' | 'off' = i.desktopMotion === 'system' ? (i.prefersReduced ? 'reduced' : 'full') : i.desktopMotion;
  const s = MOTION_SCALE[m];
  // The warp flash keeps a faint cue at Off (you still see that you jumped); everything else scales to zero.
  return { shake: s, streaks: s, fovKick: s, dust: s, tunnelSpin: s, warpFlash: m === 'full' ? 1 : m === 'reduced' ? 0.5 : 0.2 };
}

// ── What a headset banks ──────────────────────────────────────────────────────────────────────────────────────────
export const HEADSET_BANK_REDUCED = 0.35;
export const HEADSET_BANK_REDUCED_MAX = (15 * Math.PI) / 180;
/** The rig's bank in a headset for the AERO LAW's coordinated bank `bank` (rad). */
export function headsetBank(bank: number, mode: SpaceBank): number {
  if (!Number.isFinite(bank)) return 0;
  if (mode === 'full') return bank;
  if (mode === 'reduced') return Math.sign(bank) * Math.min(Math.abs(bank) * HEADSET_BANK_REDUCED, HEADSET_BANK_REDUCED_MAX);
  return 0;
}

// ── Roll comfort ──────────────────────────────────────────────────────────────────────────────────────────────────
/** Seconds without a roll input before the assist starts levelling. */
export const ROLL_ASSIST_IDLE_S = 1.5;
/** The assist's fastest roll back to level (rad/s ≈ 25°/s) and the angle inside which it slows down (≈ 10°). */
export const ROLL_ASSIST_MAX = (25 * Math.PI) / 180;
export const ROLL_ASSIST_EASE = (10 * Math.PI) / 180;
/** The slowest the assist ever rolls (rad/s ≈ 2°/s) — an eased approach must still arrive. */
export const ROLL_ASSIST_MIN = (2 * Math.PI) / 180;

/** Your roll input's scale: 1 on the desktop; the comfort cap in a headset; 0 under the horizon lock. */
export function rollRateScale(presenting: boolean, c: Pick<XRComfort, 'spaceRoll' | 'spaceRollRate'>): number {
  if (!presenting) return 1;
  if (c.spaceRoll === 'lock') return 0;
  const r = c.spaceRollRate;
  return Number.isFinite(r) ? Math.min(1, Math.max(0.25, r)) : 0.6;
}

/** Which levelling runs this tick: none · assist (eased, rate-capped) · lock (held level every tick). */
export function levelMode(presenting: boolean, roll: SpaceRoll, rollIdleS: number, levelHeld: boolean): 'none' | 'assist' | 'lock' {
  if (!presenting || levelHeld) return 'none'; // the R key / A button levels the classic way — the assist never fights it
  if (roll === 'lock') return 'lock';
  if (roll === 'assist' && rollIdleS >= ROLL_ASSIST_IDLE_S) return 'assist';
  return 'none';
}

/**
 * The slerp fraction toward level that rolls at most `maxRate·dt` along an `angle` (rad) still to go, slowing inside
 * `ease` (an eased arrival, no snap), never slower than ROLL_ASSIST_MIN. 1 = arrive this tick.
 */
export function assistSlerpT(angle: number, dt: number, maxRate = ROLL_ASSIST_MAX, ease = ROLL_ASSIST_EASE): number {
  if (!(angle > 1e-6) || !(dt > 0)) return angle > 1e-6 ? 0 : 1;
  const rate = Math.max(ROLL_ASSIST_MIN, maxRate * Math.min(1, angle / Math.max(1e-6, ease)));
  const step = Math.min(angle, rate * dt);
  return step / angle;
}

// ── The vignette's cue: the ship's motion, never the head ─────────────────────────────────────────────────────────
export const CUE = {
  /** Linear acceleration (m/s²) the vignette ignores, and where it saturates. A boost is ~25–60 m/s². */
  ACCEL_DEAD: 3,
  ACCEL_FULL: 40,
  /** Angular rate (deg/s) the vignette ignores, and where it saturates. A Lancer's full turn is ~130°/s. */
  ANG_DEAD: 6,
  ANG_FULL: 70,
  /** Roll counts this much more than yaw / pitch (the most sickening axis). */
  ROLL_WEIGHT: 1.5,
  /** A sample older than this is rest (a menu, a pause, the sector gone). */
  STALE_MS: 500,
  /** EMA time constant for the measured rates (s). */
  TAU_S: 0.12,
  /** One frame's change past these is a discontinuity (a rebuild, a warp seat), never motion. An ANGULAR step is a
   *  jump only when it is also faster than any hull turns (JUMP_RATE): under a slow renderer (the IWER probe's ~1 s
   *  frames) a real 90°/s turn is 90° a frame and read as a seat — the vignette never closed in the emulator. */
  JUMP_DV: 3000,
  JUMP_DANG: Math.PI / 3,
  JUMP_RATE: 6,
  /** A frame longer than this is a stall (a hitch, a pause) — re-seat the history, measure nothing. */
  MAX_DT_S: 2.5,
} as const;

export interface V3 { x: number; y: number; z: number }
export interface Q4 { x: number; y: number; z: number; w: number }
export interface SectorMotionState { accel: number; yawDps: number; pitchDps: number; rollDps: number; at: number; samples: number; jumps: number }

const clamp01 = (v: number): number => (v <= 0 || !Number.isFinite(v) ? 0 : v >= 1 ? 1 : v);

/** Measures the ship's ARTIFICIAL motion — its velocity's change and its orientation's rotation, in body axes. */
export class SectorMotionMeter {
  private pv: V3 | null = null;
  private pq: Q4 | null = null;
  readonly s: SectorMotionState = { accel: 0, yawDps: 0, pitchDps: 0, rollDps: 0, at: 0, samples: 0, jumps: 0 };

  /** One frame: `vel` (m/s, any fixed frame), `q` the orientation the eye rides, `nowMs` the clock. */
  sample(dt: number, vel: V3, q: Q4, nowMs: number): void {
    // THE GETTER LAW: a THREE.Quaternion keeps its numbers in `_x/_y/_z/_w` behind prototype getters — a spread copies
    // the private fields and `x` reads undefined (every sample was a NaN "jump" in the first IWER run). Copy by name.
    if (!(dt > 0) || dt > CUE.MAX_DT_S) { this.keep(vel, q); this.s.at = nowMs; return; }
    const k = 1 - Math.exp(-dt / CUE.TAU_S);
    let accel = 0, yaw = 0, pitch = 0, roll = 0, jump = false;
    if (this.pv) {
      const dv = Math.hypot(vel.x - this.pv.x, vel.y - this.pv.y, vel.z - this.pv.z);
      if (!Number.isFinite(dv) || dv > CUE.JUMP_DV) jump = true; else accel = dv / dt;
    }
    if (this.pq) {
      // dq = pq⁻¹ · q — the rotation since the last frame, expressed in the BODY (previous) axes.
      const a = this.pq;
      const ix = -a.x, iy = -a.y, iz = -a.z, iw = a.w;
      let w = iw * q.w - ix * q.x - iy * q.y - iz * q.z;
      let x = iw * q.x + ix * q.w + iy * q.z - iz * q.y;
      let y = iw * q.y - ix * q.z + iy * q.w + iz * q.x;
      let z = iw * q.z + ix * q.y - iy * q.x + iz * q.w;
      if (w < 0) { w = -w; x = -x; y = -y; z = -z; } // the short way round
      const ang = 2 * Math.acos(Math.min(1, w));
      if (!Number.isFinite(ang) || (ang > CUE.JUMP_DANG && ang / dt > CUE.JUMP_RATE)) jump = true;
      else if (ang > 1e-9) {
        const sn = Math.sqrt(Math.max(1e-12, 1 - w * w));
        const r = (ang / dt) * (180 / Math.PI) / sn;
        pitch = x * r; yaw = y * r; roll = z * r;
      }
    }
    this.keep(vel, q);
    this.s.at = nowMs;
    this.s.samples++;
    if (jump) { this.s.jumps++; this.s.accel = 0; this.s.yawDps = 0; this.s.pitchDps = 0; this.s.rollDps = 0; return; }
    this.s.accel += (accel - this.s.accel) * k;
    this.s.yawDps += (yaw - this.s.yawDps) * k;
    this.s.pitchDps += (pitch - this.s.pitchDps) * k;
    this.s.rollDps += (roll - this.s.rollDps) * k;
  }

  private keep(vel: V3, q: Q4): void {
    this.pv = { x: vel.x, y: vel.y, z: vel.z };
    this.pq = { x: q.x, y: q.y, z: q.z, w: q.w };
  }

  /** Forget the history (the sector rebuilt, a session ended). */
  reset(): void { this.pv = null; this.pq = null; this.s.accel = 0; this.s.yawDps = 0; this.s.pitchDps = 0; this.s.rollDps = 0; }
}

/** The vignette's 0..1 linear and angular terms for a motion sample under a cue. */
export function cueTerms(m: Pick<SectorMotionState, 'accel' | 'yawDps' | 'pitchDps' | 'rollDps'>, cue: SpaceCue): { lin: number; ang: number } {
  if (cue === 'off') return { lin: 0, ang: 0 };
  const lin = cue === 'all' ? clamp01((m.accel - CUE.ACCEL_DEAD) / (CUE.ACCEL_FULL - CUE.ACCEL_DEAD)) : 0;
  const a = Math.max(Math.abs(m.yawDps), Math.abs(m.pitchDps), Math.abs(m.rollDps) * CUE.ROLL_WEIGHT);
  const ang = clamp01((a - CUE.ANG_DEAD) / (CUE.ANG_FULL - CUE.ANG_DEAD));
  return { lin, ang };
}

/**
 * The sector's motion as the ground vignette's `RigMotion` (riding): the linear and angular terms are mapped onto the
 * ride references exactly, so `vignetteTarget` reproduces them — one vignette model, one Comfort-page strength.
 * Null when the sample is stale (a menu, a pause, no sector).
 */
export function sectorRigMotion(m: SectorMotionState | null, cue: SpaceCue, nowMs: number): RigMotion | null {
  if (!m || m.samples === 0 || nowMs - m.at > CUE.STALE_MS) return null;
  const { lin, ang } = cueTerms(m, cue);
  return {
    speedMps: VIGNETTE_SPEED_DEAD_MPS + lin * (VIGNETTE_SPEED_FULL_RIDE_MPS - VIGNETTE_SPEED_DEAD_MPS),
    yawRateDps: VIGNETTE_YAW_DEAD_DPS + ang * (VIGNETTE_YAW_FULL_RIDE_DPS - VIGNETTE_YAW_DEAD_DPS),
    riding: true,
  };
}

/** The ONE meter: SpaceModule feeds it every frame in flight; the XR module's vignette reads it in the sector. */
const meter = new SectorMotionMeter();
export function sectorMeter(): SectorMotionMeter { return meter; }
export function sectorRigMotionNow(cue: SpaceCue, nowMs: number = typeof performance !== 'undefined' ? performance.now() : Date.now()): RigMotion | null {
  return sectorRigMotion(meter.s, cue, nowMs);
}
